'use client';

import {
  useQuery,
  useInfiniteQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
  type DefaultError,
  type QueryKey,
  type InfiniteData,
} from '@tanstack/react-query';
import { useContext } from 'react';
import { useIsAuthed } from '../hooks/useIsAuthed';
import { AuthBootstrapContext } from '../components/AuthBootstrap';

function useBootstrapReady(): boolean {
  const ctx = useContext(AuthBootstrapContext);
  if (!ctx) return true;
  return ctx.status === 'ready';
}

export function useAuthedQuery<
  TQueryFnData,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryResult<TData, TError> {
  const isAuthed = useIsAuthed();
  const bootstrapReady = useBootstrapReady();
  const enabled = options.enabled ?? true;
  return useQuery({
    ...options,
    enabled: !!enabled && isAuthed && bootstrapReady,
  });
}

export function useAuthedInfiniteQuery<
  TQueryFnData,
  TError = DefaultError,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: UseInfiniteQueryOptions<
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TPageParam
  >,
): UseInfiniteQueryResult<TData, TError> {
  const isAuthed = useIsAuthed();
  const bootstrapReady = useBootstrapReady();
  const enabled = options.enabled ?? true;
  return useInfiniteQuery({
    ...options,
    enabled: !!enabled && isAuthed && bootstrapReady,
  });
}
